/**
 * The geometry of a sheet, derived from a document's page setup.
 *
 * ITS OWN MODULE, AND NOT BECAUSE IT IS TIDIER. PageElements imports @tiptap, which Jest will not
 * parse without transforming node_modules -- so anything living beside it cannot be unit tested at
 * all, and the suite fails to LOAD rather than to pass, which hides every test in the file. This is
 * pure arithmetic with no editor in it, exactly like outline.ts and watermark.ts, and that is what
 * makes it testable.
 *
 * ONE DERIVATION, READ BY FOUR THINGS: the paper draws the sheet, the pagination preview decides
 * where a page breaks, the ruler numbers the inches, and document_rendition.py lays out the PDF.
 * All four held their own copy of "8.5 by 11 with an inch round it" -- and the ruler's copy was
 * already a tenth of an inch out from the paper's before orientation and margins were a thing.
 */

/** Letter, portrait, an inch all round: how every procedure in this system was written. */
export const DEFAULT_GEOMETRY = {
    orientation: "portrait",
    marginTop: 1,
    marginBottom: 1,
    marginLeft: 1,
    marginRight: 1,
    columns: 1,
};

//: The grey band drawn between two sheets. It lives HERE, with the rest of the geometry, and
//: pagination.ts re-exports it for the consumers that already import it from there -- one value,
//: one owner, read in both places.
export const PAGE_GAP = 24;

//: The sheet, in inches. Letter only: A4 is a real request and a different one -- it changes the
//: renderer's page size, the ruler's length and every stored tab stop's meaning, and doing it
//: silently alongside orientation would be two changes wearing one name.
const SHEET_INCHES = { long: 11, short: 8.5 };

//: The gutter between two columns, in inches. A quarter inch is what a word processor uses and what
//: reads: narrower and the eye jumps the gap mid-line, wider and the columns stop being one page.
export const COLUMN_GAP_IN = 0.25;

/**
 * The geometry every part of the editor measures against, from the document's own setup.
 *
 * ONE DERIVATION, READ BY FOUR THINGS. The paper draws the sheet, the pagination preview decides
 * where a page breaks, the ruler numbers the inches and the renderer lays out the PDF -- and all
 * four were reading their own hard-coded constants. Orientation and margins are exactly the change
 * that makes four copies of "a page is 8.5 by 11 with an inch round it" disagree.
 *
 * INCHES ARE THE UNIT, pixels are derived at 96/in, which is what CSS means by an inch and what
 * the pagination preview has always counted in.
 */
export function pageGeometry(setup: Partial<typeof DEFAULT_GEOMETRY> | Record<string, any> | null | undefined) {
    const full = { ...DEFAULT_GEOMETRY, ...(setup ?? {}) } as any;
    const landscape = String(full.orientation).toLowerCase() === "landscape";
    // 1, 2 or 3 -- the same clamp PageElements applies when the attribute is read, repeated here
    // because this module is handed raw setup objects by the renderer's tests as well.
    const columns = full.columns === 2 || full.columns === 3 ? full.columns : 1;

    const widthIn = landscape ? SHEET_INCHES.long : SHEET_INCHES.short;
    const heightIn = landscape ? SHEET_INCHES.short : SHEET_INCHES.long;

    // CLAMPED SO A PAGE SURVIVES A TYPO. Margins that met in the middle would leave a text column
    // of zero width, which is not a layout -- it is an editor you cannot type in. Half the sheet
    // between them at most, so there is always something left to write on.
    const sideRoom = widthIn / 2;
    const endRoom = heightIn / 2;
    const marginLeft = Math.min(Math.max(0, full.marginLeft), sideRoom);
    const marginRight = Math.min(Math.max(0, full.marginRight), sideRoom);
    const marginTop = Math.min(Math.max(0, full.marginTop), endRoom);
    const marginBottom = Math.min(Math.max(0, full.marginBottom), endRoom);

    const pageHeight = Math.round(heightIn * 96);
    return {
        landscape,
        widthIn, heightIn,
        marginTop, marginBottom, marginLeft, marginRight,
        /** The text column, in inches. What the ruler numbers and what an indent is measured into. */
        textWidthIn: Math.max(0.5, widthIn - marginLeft - marginRight),
        pageHeight,
        sheetPitch: pageHeight + PAGE_GAP,
        /** The writable height of one sheet: what the pagination preview breaks against. */
        textHeight: Math.max(96, pageHeight - Math.round((marginTop + marginBottom) * 96)),
        columns,
        columnGapIn: columns > 1 ? COLUMN_GAP_IN : 0,
        /**
         * One column's width, in inches: the measure a line of text is actually set to.
         *
         * THE RENDERER READS THE SAME NUMBER. ReportLab is given a frame per column and the browser
         * CSS multi-column; both have to arrive at the same measure or the screen and the signed PDF
         * disagree about where every line wraps, which is the thing this module exists to prevent.
         */
        columnWidthIn: columns > 1
            ? Math.max(0.5, (Math.max(0.5, widthIn - marginLeft - marginRight)
                             - COLUMN_GAP_IN * (columns - 1)) / columns)
            : Math.max(0.5, widthIn - marginLeft - marginRight),
    };
}

